import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Nav, Footer } from '../components/Chrome';
import { useGetApp } from '../components/GetTheApp';
import {
  entryLabel,
  getComments,
  getEntries,
  getEntry,
  getSeries,
  getUserById,
  isEntryLockedOnWeb,
  readAccessOf,
} from '../firebase/reads';
import { parseEntryContent, stripImageTokens } from '../entryContent';
import { usePageMeta } from '../usePageMeta';

const LOCK_REASON = {
  friends: 'The author has kept the rest of this for their friends.',
  subscribers: 'The rest of this is for subscribers — subscribing is free.',
  private: 'The author has kept this one to themselves.',
};

/**
 * longreads.me/read/<seriesId>/<entryId> — the reader.
 *
 * This is the page a shared link lands on, so it has to do the gating
 * correctly: the documents are world-readable in Firestore and the only thing
 * standing between an anonymous visitor and a subscribers-only chapter is this
 * check. `isEntryLockedOnWeb` mirrors the app's `_isEntryGated` with
 * hasAccess permanently false.
 */
function Entry() {
  const { seriesId, entryId } = useParams();
  const getApp = useGetApp();

  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      try {
        // Series and entry are independent reads, so fetch them together. They
        // used to run one after the other, which made the page three network
        // round trips deep before anything could render.
        const [series, entry] = await Promise.all([
          getSeries(seriesId),
          getEntry(seriesId, entryId),
        ]);
        if (cancelled) return;
        if (!series || !entry) {
          setState({ status: 'missing' });
          return;
        }

        const locked = isEntryLockedOnWeb(series, entry);
        // Comments are only fetched for something the visitor may read. A
        // locked chapter's comments would otherwise leak its contents.
        const [author, siblings, comments] = await Promise.all([
          getUserById(series.authorId),
          getEntries(seriesId),
          locked ? Promise.resolve([]) : getComments(seriesId, entryId),
        ]);
        if (cancelled) return;

        setState({ status: 'ready', series, entry, author, siblings, comments, locked });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [seriesId, entryId]);

  const { status, series, entry, author, siblings = [], comments = [], locked } = state;

  usePageMeta(
    entry ? `${entry.title || entryLabel(entry)} · Long reads` : 'Long reads',
    entry && !locked ? stripImageTokens(entry.content).slice(0, 200) : 'Read it on Long reads.'
  );

  const index = siblings.findIndex((e) => e.id === entryId);
  const prev = index > 0 ? siblings[index - 1] : null;
  const next = index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : null;

  return (
    <div className="app">
      <Nav />
      <main className="page">
        {status === 'loading' && <p className="state">Loading…</p>}
        {status === 'error' && <p className="state">Could not load this entry.</p>}
        {status === 'missing' && <p className="state">This entry isn’t available.</p>}

        {status === 'ready' && (
          <>
            <p className="reader-label">{entryLabel(entry)}</p>
            <h1 className="reader-title">{entry.title || series.title}</h1>
            <p className="reader-byline">
              <Link to={`/read/${seriesId}`} style={{ color: 'inherit' }}>
                {series.title}
              </Link>
              {author?.username && (
                <>
                  {' · '}
                  <Link to={`/${author.username}`} style={{ color: 'inherit' }}>
                    @{author.username}
                  </Link>
                </>
              )}
            </p>

            {locked ? (
              <div className="locked">
                <h2>This part is locked</h2>
                <p>{LOCK_REASON[readAccessOf(series)] || 'The author has restricted this entry.'}</p>
                <button className="btn btn-primary" onClick={() => getApp('read the rest')}>
                  Get the app
                </button>
              </div>
            ) : (
              <article>
                {parseEntryContent(entry.content).map((block, i) =>
                  block.kind === 'text' ? (
                    <p className="prose" key={i}>
                      {block.text}
                    </p>
                  ) : block.scratch ? (
                    // Scratch-to-reveal is a gesture the app owns. Showing the
                    // image here would give away what the author chose to hide.
                    <div className="scratch" key={i}>
                      <img src={block.url} alt="" />
                      <button
                        className="scratch-veil"
                        onClick={() => getApp('scratch this one off')}
                      >
                        Hidden image
                        <small>Scratch it off in the app</small>
                      </button>
                    </div>
                  ) : (
                    <img className="prose-image" src={block.url} alt="" key={i} />
                  )
                )}
              </article>
            )}

            <div className="rail">
              <button className="btn" onClick={() => getApp('like this')}>
                ♡ {entry.likes || 0}
              </button>
              <button className="btn" onClick={() => getApp('save this')}>
                Save
              </button>
              <button className="btn" onClick={() => getApp('join the conversation')}>
                Comment
              </button>
            </div>

            <div className="pager">
              {prev ? (
                <Link to={`/read/${seriesId}/${prev.id}`}>← {entryLabel(prev)}</Link>
              ) : (
                <span />
              )}
              {next ? (
                <Link to={`/read/${seriesId}/${next.id}`}>{entryLabel(next)} →</Link>
              ) : (
                <span />
              )}
            </div>

            {!locked && comments.length > 0 && (
              <section className="comments">
                <h2 className="section-label">COMMENTS</h2>
                {comments.map((c) => (
                  <div className="comment" key={c.id}>
                    <p className="comment-who">@{c.username || 'someone'}</p>
                    <p className="comment-text">{c.text}</p>
                  </div>
                ))}
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default Entry;
